import { useState, useCallback } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import ResponsibleNotice from '@/components/feature/ResponsibleNotice';
import StatsOverview from './components/StatsOverview';
import FrequencyChart from './components/FrequencyChart';
import HotColdNumbers from './components/HotColdNumbers';
import RecentDraws from './components/RecentDraws';
import PairAnalysis from './components/PairAnalysis';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import AdSlot from '@/components/feature/AdSlot';
import DrawDataStatus from '@/components/feature/DrawDataStatus';
import PeriodFilter from './components/PeriodFilter';
import AdvancedFilterBar, { type AdvancedFilters } from './components/AdvancedFilterBar';
import StatisticsDataProvider from './components/StatisticsDataProvider';
import { useStatisticsData } from './StatisticsDataContext';
import { usePageMeta } from '@/hooks/usePageMeta';
import { EMPTY_ADVANCED_FILTERS } from '@/lib/statisticsAnalysis';

interface StatisticsContentProps {
  periodLimit: number | null;
  setPeriodLimit: (v: number | null) => void;
  advancedFilters: AdvancedFilters;
  setAdvancedFilters: (v: AdvancedFilters) => void;
  onResetFilters: () => void;
}

function StatisticsContent({
  periodLimit,
  setPeriodLimit,
  advancedFilters,
  setAdvancedFilters,
  onResetFilters,
}: StatisticsContentProps) {
  const {
    loadedCount,
    periodFilteredDraws,
    filteredDraws,
    invalidCount,
    loading,
    error,
    retry,
  } = useStatisticsData();

  const totalAllDraws = loadedCount;
  const totalPeriodDraws = periodFilteredDraws.length;
  const totalFilteredDraws = filteredDraws.length;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100/70 text-accent-700 text-xs font-medium mb-4">
              <i className="ri-bar-chart-line"></i>
              <span>데이터 기반 분석</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-2">
              통계 분석 대시보드
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mb-6">
              과거 당첨번호 데이터를 분석하여 출현 빈도, 핫/콜드 넘버, 최근 당첨 이력을 한눈에 확인하세요.
            </p>
            <PeriodFilter
              value={periodLimit}
              onChange={setPeriodLimit}
              totalDraws={totalAllDraws}
            />
            <div className="mt-4">
              <AdvancedFilterBar
                filters={advancedFilters}
                onChange={setAdvancedFilters}
                totalFilteredDraws={totalFilteredDraws}
                totalAllDraws={totalPeriodDraws}
              />
            </div>
          </div>
        </section>

        <ResponsibleNotice />

        {/* 데이터 현황 (최신성·완전성) */}
        <section className="w-full px-4 md:px-6 pb-6">
          <div className="max-w-6xl mx-auto">
            <DrawDataStatus
              loadedCount={loadedCount}
              analyzedCount={totalFilteredDraws}
              invalidCount={invalidCount}
              loading={loading}
            />
          </div>
        </section>

        {/* 로드 오류 + 재시도 (공통 처리) */}
        {error ? (
          <section className="w-full px-4 md:px-6 pb-8">
            <div className="max-w-6xl mx-auto">
              <div className="rounded-2xl border border-accent-200 bg-accent-50 p-10 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-2xl text-accent-600"></i>
                </div>
                <h2 className="font-heading text-lg text-foreground-800 mb-1">데이터를 불러오지 못했습니다</h2>
                <p className="text-sm text-foreground-500 mb-5">{error}</p>
                <button
                  onClick={retry}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  다시 시도
                </button>
              </div>
            </div>
          </section>
        ) : (
          <>
            {/* Stats Overview */}
            <section className="w-full px-4 md:px-6 pb-8">
              <div className="max-w-6xl mx-auto">
                <StatsOverview />
              </div>
            </section>

            {/* Ad Slot 1 — 상단 배너 */}
            <section className="w-full px-4 md:px-6 py-6">
              <div className="max-w-6xl mx-auto">
                <AdSlot type="kakao" width={728} height={90} />
              </div>
            </section>

            {/* Hot & Cold Numbers */}
            <section className="w-full px-4 md:px-6 py-8">
              <div className="max-w-6xl mx-auto">
                <HotColdNumbers />
              </div>
            </section>

            {/* Ad Slot 2 — 중간 배너 */}
            <section className="w-full px-4 md:px-6 py-6">
              <div className="max-w-6xl mx-auto">
                <AdSlot type="google" width={728} height={90} />
              </div>
            </section>

            {/* Pair Analysis & Deep Stats */}
            <section className="w-full px-4 md:px-6 py-8">
              <div className="max-w-6xl mx-auto">
                <PairAnalysis />
              </div>
            </section>

            {/* Frequency Chart & Recent Draws */}
            <section className="w-full px-4 md:px-6 py-8">
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-3 bg-background-100/80 border border-background-200/60 rounded-2xl p-6">
                    <FrequencyChart />
                  </div>
                  <div className="lg:col-span-2 bg-background-100/80 border border-background-200/60 rounded-2xl p-6">
                    <RecentDraws />
                  </div>
                </div>
              </div>
            </section>

            {/* Ad Slot 3 — 하단 배너 */}
            <section className="w-full px-4 md:px-6 py-6">
              <div className="max-w-6xl mx-auto">
                <AdSlot type="kakao" width={728} height={90} />
              </div>
            </section>

            {/* CTA - go to number generator */}
            <section className="w-full px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary-800 via-primary-700 to-secondary-700 rounded-3xl p-10 md:p-14 text-center text-white">
                <h2 className="font-heading text-2xl md:text-3xl mb-4">
                  분석은 끝, 이제 번호를 골라볼 시간!
                </h2>
                <p className="text-white/80 text-sm md:text-base mb-8 max-w-md mx-auto">
                  통계 데이터를 참고해서 나만의 행운 번호를 만들어보세요.
                  랜덤 생성기로 바로 가실까요?
                </p>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    window.REACT_APP_NAVIGATE('/');
                  }}
                  className="
                    inline-flex items-center gap-2 px-8 py-3.5 rounded-full
                    bg-accent-500 text-accent-950 font-bold text-base
                    hover:bg-accent-600 transition-all duration-200
                    whitespace-nowrap cursor-pointer
                  "
                >
                  <i className="ri-shuffle-line"></i>
                  번호 생성하러 가기
                </a>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
      <div className="md:hidden h-16"></div>
      <MobileBottomNav />
    </div>
  );
}

export default function Statistics() {
  const [periodLimit, setPeriodLimit] = useState<number | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(EMPTY_ADVANCED_FILTERS);

  const handleResetFilters = useCallback(() => {
    setAdvancedFilters(EMPTY_ADVANCED_FILTERS);
  }, []);

  usePageMeta({
    title: '로또 통계 분석 대시보드 | LottoLab — 당첨번호 빈도·패턴·추천',
    description:
      'LottoLab 로또 통계 분석 대시보드에서 역대 당첨번호 빈도 분석, 핫·콜드 넘버, 번호 쌍 패턴, 최근 당첨 이력을 확인하세요. 데이터 기반 로또 번호 분석과 스마트 추천으로 더 똑똑한 번호 선택을 도와드립니다.',
    keywords:
      '로또 통계, 로또 번호 분석, 로또 빈도 분석, 당첨번호 통계, 로또 핫콜드 넘버, 로또 번호 추천, LottoLab, 로또랩',
    canonical: 'https://lottolab.app/statistics',
  });

  return (
    <StatisticsDataProvider
      periodLimit={periodLimit}
      advancedFilters={advancedFilters}
      onResetFilters={handleResetFilters}
    >
      <StatisticsContent
        periodLimit={periodLimit}
        setPeriodLimit={setPeriodLimit}
        advancedFilters={advancedFilters}
        setAdvancedFilters={setAdvancedFilters}
        onResetFilters={handleResetFilters}
      />
    </StatisticsDataProvider>
  );
}